import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Megaphone, Save } from "lucide-react";

interface Announcement {
  id: string;
  text: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export default function AnnouncementManagement() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const { toast } = useToast();

  const fetchAnnouncements = async () => {
    const { data, error } = await supabase
      .from("announcements")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setAnnouncements(data);
    setLoading(false);
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleAdd = async () => {
    if (!newText.trim()) return;
    const { error } = await supabase.from("announcements").insert({ text: newText.trim() });
    if (error) {
      toast({ title: "添加失败", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "已添加公告" });
      setNewText("");
      fetchAnnouncements();
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    const { error } = await supabase.from("announcements").update({ enabled }).eq("id", id);
    if (!error) {
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, enabled } : a));
    }
  };

  const handleUpdateText = async (id: string, text: string) => {
    const { error } = await supabase.from("announcements").update({ text }).eq("id", id);
    if (error) {
      toast({ title: "更新失败", variant: "destructive" });
    } else {
      toast({ title: "已保存" });
      fetchAnnouncements();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("announcements").delete().eq("id", id);
    if (!error) {
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast({ title: "已删除" });
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Megaphone className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">公告管理</h1>
      </div>

      {/* Add new */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">添加新公告</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="输入公告内容，如：🎉 新店开业活动..."
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
            />
            <Button onClick={handleAdd} disabled={!newText.trim()}>
              <Plus className="h-4 w-4 mr-1" /> 添加
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <div className="space-y-3">
        {announcements.map(a => (
          <AnnouncementRow
            key={a.id}
            announcement={a}
            onToggle={handleToggle}
            onSave={handleUpdateText}
            onDelete={handleDelete}
          />
        ))}
        {announcements.length === 0 && (
          <p className="text-muted-foreground text-center py-8">暂无公告</p>
        )}
      </div>
    </div>
  );
}

function AnnouncementRow({
  announcement,
  onToggle,
  onSave,
  onDelete,
}: {
  announcement: Announcement;
  onToggle: (id: string, enabled: boolean) => void;
  onSave: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) {
  const [text, setText] = useState(announcement.text);
  const changed = text !== announcement.text;

  return (
    <Card className={!announcement.enabled ? "opacity-60" : ""}>
      <CardContent className="flex items-center gap-3 py-4">
        <Switch
          checked={announcement.enabled}
          onCheckedChange={v => onToggle(announcement.id, v)}
        />
        <Input
          className="flex-1"
          value={text}
          onChange={e => setText(e.target.value)}
        />
        <Button
          size="icon"
          variant="ghost"
          disabled={!changed}
          onClick={() => onSave(announcement.id, text)}
        >
          <Save className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="text-destructive"
          onClick={() => onDelete(announcement.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
